import {
  DEFAULT_GAS_LIMIT,
  PeripheryCategory,
  StreamCategory,
  whitelist,
} from "@sablier/v2-constants";
import { _ } from "@sablier/v2-mixins";
import { vendors } from "@sablier/v2-utils";
import {
  FEATURE_FLASH_LOANS_ENABLED,
  contracts,
  peripheries,
} from "~/client/constants";
import { Extension } from "../extensions";

export function isFlashToken(
  chainId?: keyof typeof contracts | number,
  asset?: string,
): boolean {
  if (
    _.isNilOrEmptyString(chainId) ||
    _.isNilOrEmptyString(asset) ||
    _.isNilOrEmptyString(whitelist.flash?.[chainId])
  ) {
    return false;
  }
  return whitelist.flash[chainId].some(
    (fl) => _.toLower(fl.address) === _.toLower(asset),
  );
}

export function contractor(
  chainId: keyof typeof contracts | number | undefined,
  purpose: Extension | StreamCategory = StreamCategory.LOCKUP_LINEAR,
  asset?: string | undefined,
) {
  try {
    if (
      !_.isNilOrEmptyString(chainId) &&
      !_.isNilOrEmptyString(contracts?.[chainId])
    ) {
      const isFlash =
        FEATURE_FLASH_LOANS_ENABLED && isFlashToken(chainId, asset);

      const isLockupLinear = [
        Extension.CLIFF.toLowerCase(),
        Extension.LINEAR.toLowerCase(),
        StreamCategory.LOCKUP_LINEAR.toLowerCase(),
      ].includes(purpose.toLowerCase());

      const flavor = (() => {
        if (isFlash) {
          return isLockupLinear
            ? `${StreamCategory.LOCKUP_LINEAR}Flash`
            : `${StreamCategory.LOCKUP_DYNAMIC}Flash`;
        }

        return isLockupLinear
          ? StreamCategory.LOCKUP_LINEAR
          : StreamCategory.LOCKUP_DYNAMIC;
      })();

      return contracts[chainId][flavor];
    }
  } catch (error) {
    vendors.crash.log(error);
  }
  return { alias: "", address: "", methods: {} };
}

export function peripheral(
  chainId?: keyof typeof peripheries | number,
  purpose: PeripheryCategory = PeripheryCategory.TARGET,
) {
  try {
    if (
      !_.isNilOrEmptyString(chainId) &&
      !_.isNilOrEmptyString(peripheries?.[chainId]?.[purpose])
    ) {
      return peripheries[chainId][purpose];
    }
  } catch (error) {
    vendors.crash.log(error);
  }
  return { address: "", alias: "", methods: {} };
}

export function gasser(
  chainId?: keyof typeof contracts | number,
  purpose?: Extension | StreamCategory | undefined,
  method?: string,
  multiplier?: number,
): number {
  try {
    if (
      !_.isNilOrEmptyString(purpose) &&
      !_.isNilOrEmptyString(chainId) &&
      !_.isNilOrEmptyString(method)
    ) {
      const isPeriphery = Object.values(PeripheryCategory).find(
        (p) => p === purpose,
      );

      const contract = isPeriphery
        ? peripheral(chainId, purpose)
        : contractor(chainId, undefined, purpose);

      if (
        !_.isNil(contract) &&
        !_.isNil(contract.methods) &&
        _.has(contract.methods, method)
      ) {
        const limit = contract.methods[method];
        if (_.isFunction(limit)) {
          return limit(multiplier || 1);
        }
        return limit;
      }
    }
  } catch (error) {
    vendors.crash.log(error);
  }

  return DEFAULT_GAS_LIMIT;
}
